/**
 * New-session setup: airport, traffic, realism, display and voice. Reads and returns
 * SessionSettings; starting the session is the caller's job. The footer sums the choices up as
 * a flight strip with the Start button at its end.
 */
import { clockText, metarLine, REALISM, resolveConditions, startHours, TIME_OF_DAY_LABEL, TIMES_OF_DAY, WEATHER, WEATHERS, type AirportData, type RealismLevel, type TimeOfDay, type Weather } from '@atc/core';
import { SCENARIOS, challengeRecords } from '../scenarios';
import { AIRPORTS, loadAirport } from '../airports';
import { describeRealism } from '../realism-copy';
import { VIEWS } from '../game';
import { browserStorage, TRAFFIC, type Layout, type SessionSettings, type Traffic } from '../settings';
import { mountMenuNavigation } from '../menu-navigation';

const REALISM_LABEL: Record<RealismLevel, string> = { low: 'Low', mid: 'Mid', high: 'High' };
const REALISM_BARS: Record<RealismLevel, number> = { low: 1, mid: 2, high: 3 };
const TRAFFIC_LABEL: Record<Traffic, string> = { light: 'Light', normal: 'Normal', busy: 'Busy' };
const MODES = [
  { value: 'shift15', label: '15 minute shift', hint: 'A short tower shift: receive arrivals, find departure gaps, and hand over the position.' },
  { value: 'scenario', label: 'Challenge', hint: 'A short, repeatable traffic puzzle. Read the briefing and keep everyone safe.' },
  { value: 'free', label: 'Free play', hint: 'No clock. Type QUIT or choose End session in the menu to see your statistics.' },
  { value: 'shift30', label: '30 minute shift', hint: 'Thirty minutes of sim time, then a debrief with a grade and your best shifts. SPEED shortens the wait.' },
  { value: 'shift60', label: '60 minute shift', hint: 'An hour of sim time, then a debrief with a grade and your best shifts. SPEED shortens the wait.' },
];
const GRAPHICS: [string, string][] = [['auto', 'Automatic'], ['3', 'High'], ['2', 'Balanced'], ['1', 'Low'], ['0', 'Minimal']];
const SETUP_STEPS = [
  { id: 'assignment', label: 'Assignment', note: 'Choose how this watch will run' },
  { id: 'airfield', label: 'Airfield', note: 'Take a position and configure runways' },
  { id: 'workload', label: 'Workload', note: 'Set traffic and operating realism' },
  { id: 'console', label: 'Console', note: 'Prepare the display and radio' },
] as const;
type SetupStep = typeof SETUP_STEPS[number]['id'];

export function showSetup(root: HTMLElement, options: { settings: SessionSettings; allowVoicePlayback?: boolean; allowSpeechInput?: boolean; onStart(settings: SessionSettings): void }): () => void {
  const s = options.settings;
  const radio = (name: string, value: string, checked: boolean) => `<input type="radio" name="${name}" value="${value}"${checked ? ' checked' : ''}>`;
  const check = (name: string, checked: boolean, label: string, note: string, disabled = false) => `<label class="check"><input type="checkbox" name="${name}"${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}><span>${label}<small>${note}</small></span></label>`;
  const cell = (key: string, label: string) => `<p class="cstrip-cell"><small>${label}</small><b data-sum="${key}"></b></p>`;
  root.innerHTML = `
  <form class="board setup">
    <header class="screen-head">
      <a class="screen-back" href="#/">Main menu</a>
      <div class="screen-title"><p>Position briefing</p><h1>New session</h1></div>
      <p class="screen-aside">Configure the watch, then take the frequency</p>
    </header>
    <div class="setup-console">
      <nav class="setup-steps" aria-label="Session setup stages">${SETUP_STEPS.map((step, i) => `
        <button type="button" data-step-tab="${step.id}"${i === 0 ? ' aria-current="step"' : ''}>
          <span>${i + 1}</span><b>${step.label}</b><small>${step.note}</small>
        </button>`).join('')}</nav>
      <div class="setup-stage" aria-live="polite">
        <section class="setup-step" data-step="assignment">
          <header class="setup-step-head"><span>01</span><div><h2>Choose your assignment</h2><p>Every watch asks for a different kind of concentration.</p></div></header>
          <fieldset class="panel setup-session">
            <legend>Session type</legend>
            <div class="assignment-grid">${MODES.map((m) => `<label class="assignment-card">${radio('mode', m.value, m.value === (s.mode === 'shift' ? `shift${s.shiftMinutes}` : s.mode))}<span><b>${m.label}</b><small>${m.hint}</small></span></label>`).join('')}</div>
            <p class="hint" data-mode-hint></p>
            <fieldset class="sub" data-scenario-choice>
              <legend>Challenge briefing</legend>
              <select name="scenario">${SCENARIOS.map((v) => `<option value="${v.id}"${v.id === s.scenario ? ' selected' : ''}>${v.title}</option>`).join('')}</select>
              <p class="hint" data-scenario-hint></p>
            </fieldset>
          </fieldset>
        </section>

        <section class="setup-step" data-step="airfield" hidden>
          <header class="setup-step-head"><span>02</span><div><h2>Select the airfield</h2><p>Your airport and position define the traffic picture.</p></div></header>
          <div class="airfield-grid">
            <fieldset class="panel airport-panel"><legend>Airport</legend>
              <div class="strip-list">${AIRPORTS.map((a) => `
                <label class="strip strip-arr strip-choice">
                  ${radio('airport', a.icao, a.icao === s.airport)}
                  <span class="strip-cs">${a.icao.toUpperCase()}</span>
                  <span class="strip-text"><b>${a.name}</b><br>${a.city}</span>
                  <span class="strip-meta" data-runways="${a.icao}">Loading runways</span>
                </label>`).join('')}
              </div>
            </fieldset>
            <fieldset class="panel setup-session"><legend>Position and runways</legend>
          <fieldset class="sub">
            <legend>Controller position</legend>
            <div class="seg seg-row"><label>${radio('position', 'tower', s.position !== 'legacy' && s.airport === 'ksfo')}<span>Tower</span></label><label>${radio('position', 'legacy', s.position === 'legacy' || s.airport !== 'ksfo')}<span>Original combined</span></label></div>
            <p class="hint" data-position-hint>Tower runs the runway; approach and ground are handled by your colleagues.</p>
            ${check('coaching', s.coaching !== false, 'Runway coaching', 'Warn before unsafe runway clearances. With coaching off, you take responsibility for executable clearances.')}
          </fieldset>
          <fieldset class="sub" data-runway-choice>
            <legend>Runways in use</legend>
            <p class="hint">Choose one or more for each operation. The same runway may serve both.</p>
            <div class="runway-picks" data-runway-picks>Loading runway choices…</div>
          </fieldset>
          <fieldset class="sub" data-conditions>
            <legend>Conditions</legend>
            <div class="seg seg-row" aria-label="Time of day">${TIMES_OF_DAY.map((t) => `<label>${radio('timeOfDay', t, t === s.timeOfDay)}<span>${TIME_OF_DAY_LABEL[t]}</span></label>`).join('')}</div>
            <div class="seg seg-row" aria-label="Weather">${WEATHERS.map((w) => `<label>${radio('weather', w, w === s.weather)}<span>${WEATHER[w].label}</span></label>`).join('')}</div>
            <p class="hint" data-conditions-hint></p>
          </fieldset>
            </fieldset>
          </div>
        </section>

        <section class="setup-step" data-step="workload" hidden>
          <header class="setup-step-head"><span>03</span><div><h2>Set the workload</h2><p>Start in the flow channel; increase pressure when the picture feels slow.</p></div></header>
          <fieldset class="panel setup-session"><legend>Traffic and realism</legend>
          <fieldset class="sub">
            <legend>Traffic</legend>
            <div class="traffic-grid">${(Object.keys(TRAFFIC) as Traffic[]).map((t) => `<label class="traffic-card">${radio('traffic', t, t === s.traffic)}<span><b>${TRAFFIC_LABEL[t]}</b><small>${TRAFFIC[t].arrivalsPerHour} arrivals / ${TRAFFIC[t].departuresPerHour} departures per hour</small></span></label>`).join('')}</div>
            <p class="hint" data-traffic-hint></p>
          </fieldset>
          <fieldset class="sub">
            <legend>Realism</legend>
            <div class="realism-row">${(['low', 'mid', 'high'] as RealismLevel[]).map((l) => `
              <label class="realism-card">${radio('realism', l, l === s.realism)}
                <span class="realism-name">${REALISM_LABEL[l]}<span class="realism-level" aria-hidden="true">${[1, 2, 3].map((n) => `<i${n <= REALISM_BARS[l] ? ' class="on"' : ''}></i>`).join('')}</span></span>
                <ul>${describeRealism(REALISM[l]).map((d) => `<li>${d}</li>`).join('')}</ul>
              </label>`).join('')}
            </div>
          </fieldset>
          </fieldset>
        </section>

        <section class="setup-step" data-step="console" hidden>
          <header class="setup-step-head"><span>04</span><div><h2>Prepare the console</h2><p>Choose what you see and hear before taking the frequency.</p></div></header>
          <div class="setup-pair">
          <fieldset class="panel">
            <legend>Display</legend>
            <fieldset class="sub">
              <legend>Layout</legend>
              <div class="seg seg-row">${VIEWS.map((v) => `<label>${radio('layout', v.layout, v.layout === s.layout)}<span>${v.label}</span></label>`).join('')}</div>
              <p class="hint" data-layout-hint></p>
            </fieldset>
            <label class="field"><span>Graphics quality</span>
              <select name="graphicsQuality">${GRAPHICS.map(([q, label]) => `<option value="${q}"${String(s.graphicsQuality) === q ? ' selected' : ''}>${label}</option>`).join('')}</select>
              <span class="hint">Independent of simulation realism.</span>
            </label>
            ${check('assists', s.assists, 'Assist', 'The aircraft card suggests the next instruction and what to say, and a hint line explains why. Good while you learn.')}
            ${check('fullscreen', s.fullscreen, 'Play in full screen', 'Esc opens the game menu; Alt+F toggles full screen')}
          </fieldset>
          <fieldset class="panel">
            <legend>Radio and sound</legend>
            <p class="hint" data-voice-capability>Checking optional radio services…</p>
            ${check('voice', s.voice, 'Read radio calls aloud', options.allowVoicePlayback === false ? 'Disabled by the current game configuration.' : s.radioProvider === 'openai' ? 'Pilot and controller voices are AI-generated. No microphone permission needed.' : 'Playback does not require microphone permission.', options.allowVoicePlayback === false)}
            ${check('speechInput', s.speechInput, 'OpenAI push to talk', options.allowSpeechInput === false ? 'Disabled by the current game configuration.' : 'Hold Space (or Alt+R) and talk as on frequency. Pilots read back; say "negative" to correct them. Audio is sent to OpenAI while transmitting.', options.allowSpeechInput === false)}
            ${check('sound', s.sound, 'Alert tones', 'A tone for traffic alerts, separation losses, landings and handoffs')}
          </fieldset>
        </div>
        </section>

        <div class="setup-stage-actions">
          <button class="btn" type="button" data-step-back>Previous</button>
          <span data-step-status>Step 1 of 4</span>
          <button class="btn-primary" type="button" data-step-next>Continue to Airfield</button>
        </div>
      </div>
    </div>
    <footer class="setup-foot">
      <div class="cstrip">
        ${cell('airport', 'Airport')}${cell('mode', 'Session')}${cell('conditions', 'Conditions')}${cell('traffic', 'Traffic')}${cell('realism', 'Realism')}${cell('layout', 'Display')}
        <button class="btn-primary" type="submit" data-launch hidden>Take the frequency</button>
      </div>
    </footer>
  </form>`;

  const form = root.querySelector<HTMLFormElement>('form.setup')!;
  let activeStep = 0;
  const showStep = (next: number, focus = true) => {
    activeStep = Math.max(0, Math.min(SETUP_STEPS.length - 1, next));
    const step = SETUP_STEPS[activeStep]!;
    form.querySelectorAll<HTMLElement>('[data-step]').forEach((el) => { el.hidden = el.dataset.step !== step.id; });
    form.querySelectorAll<HTMLButtonElement>('[data-step-tab]').forEach((button, i) => {
      if (i === activeStep) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      button.classList.toggle('is-visited', i <= activeStep);
    });
    const back = form.querySelector<HTMLButtonElement>('[data-step-back]')!;
    const nextButton = form.querySelector<HTMLButtonElement>('[data-step-next]')!;
    const launch = form.querySelector<HTMLButtonElement>('[data-launch]')!;
    back.hidden = activeStep === 0;
    nextButton.hidden = activeStep === SETUP_STEPS.length - 1;
    launch.hidden = activeStep !== SETUP_STEPS.length - 1;
    if (activeStep < SETUP_STEPS.length - 1) nextButton.textContent = `Continue to ${SETUP_STEPS[activeStep + 1]!.label}`;
    form.querySelector<HTMLElement>('[data-step-status]')!.textContent = `Step ${activeStep + 1} of ${SETUP_STEPS.length}`;
    form.dataset.activeStep = step.id;
    if (focus) form.querySelector<HTMLElement>(`[data-step="${step.id}"] input:checked, [data-step="${step.id}"] button, [data-step="${step.id}"] select`)?.focus();
  };
  form.querySelectorAll<HTMLButtonElement>('[data-step-tab]').forEach((button, i) => button.addEventListener('click', () => showStep(i)));
  form.querySelector<HTMLButtonElement>('[data-step-back]')!.addEventListener('click', () => showStep(activeStep - 1));
  form.querySelector<HTMLButtonElement>('[data-step-next]')!.addEventListener('click', () => showStep(activeStep + 1));
  if (s.position === undefined && s.airport === 'ksfo') {
    form.querySelector<HTMLInputElement>('input[name=layout][value=split]')!.checked = true;
    if (s.mode === 'free') form.querySelector<HTMLInputElement>('input[name=mode][value=shift15]')!.checked = true;
  }
  const value = (name: string) => (form.elements.namedItem(name) as RadioNodeList).value;
  const hint = form.querySelector<HTMLElement>('[data-traffic-hint]')!;
  const layoutHint = form.querySelector<HTMLElement>('[data-layout-hint]')!;
  const modeHint = form.querySelector<HTMLElement>('[data-mode-hint]')!;
  const sum = (key: string, text: string) => {
    const el = form.querySelector<HTMLElement>(`[data-sum="${key}"]`)!;
    el.textContent = text;
    el.title = text;
  };
  let runwayAirport = '', runwayGeneration = 0;
  /** The airport the conditions line was worked out for; set once its data loads. */
  let conditionsAirport: AirportData | null = null;
  const updateConditions = () => {
    const tod = value('timeOfDay') as TimeOfDay, weather = value('weather') as Weather;
    sum('conditions', `${TIME_OF_DAY_LABEL[tod]} · ${WEATHER[weather].label}`);
    const el = form.querySelector<HTMLElement>('[data-conditions-hint]')!;
    const ap = conditionsAirport?.icao.toLowerCase() === value('airport') ? conditionsAirport : null;
    if (!ap) { el.textContent = 'The clock runs with sim time. Weather sets the sky, visibility and wind strength.'; return; }
    const c = resolveConditions(ap, weather, startHours(ap, tod));
    const calm = value('mode') === 'scenario' ? ' Challenges fly without wind.' : value('realism') === 'low' ? ' Low realism flies without wind.' : '';
    el.textContent = `${metarLine(ap.icao, c, WEATHER[weather].gusty)} · local ${clockText(c.timeOfDayHours)}.${calm}`;
  };
  const runwaySelections = new Map<string, { arrivals: string[]; departures: string[] }>();
  runwaySelections.set(s.airport, { arrivals: s.arrivalRunways, departures: s.departureRunways });
  const selectedRunways = (kind: 'arrival' | 'departure') => [...form.querySelectorAll<HTMLInputElement>(`input[name=${kind}Runways]:checked`)].map((x) => x.value);
  const rememberRunways = () => {
    if (!runwayAirport) return;
    const arrivals = selectedRunways('arrival'), departures = selectedRunways('departure');
    if (arrivals.length && departures.length) runwaySelections.set(runwayAirport, { arrivals, departures });
  };
  const loadRunways = (icao: string) => {
    if (icao === runwayAirport) return;
    rememberRunways(); runwayAirport = icao; const generation = ++runwayGeneration;
    const host = form.querySelector<HTMLElement>('[data-runway-picks]')!; host.textContent = 'Loading runway choices…';
    void loadAirport(icao).then((ap) => {
      if (disposed || generation !== runwayGeneration) return;
      conditionsAirport = ap; updateConditions();
      const ends = ap.runways.flatMap((r) => r.ends.map((e) => e.name));
      const prior = runwaySelections.get(icao);
      const arrivals = prior?.arrivals.filter((x) => ends.includes(x)) ?? [ap.arrivalRunway];
      const departures = prior?.departures.filter((x) => ends.includes(x)) ?? [ap.departureRunway];
      const group = (name: 'arrival' | 'departure', picked: string[]) => `<div><b>${name === 'arrival' ? 'Arrivals' : 'Departures'}</b><div class="seg runway-seg">${ends.map((rw) => `<label><input type="checkbox" name="${name}Runways" value="${rw}"${picked.includes(rw) ? ' checked' : ''}><span>${rw}</span></label>`).join('')}</div></div>`;
      host.innerHTML = group('arrival', arrivals.length ? arrivals : [ap.arrivalRunway]) + group('departure', departures.length ? departures : [ap.departureRunway]);
    }).catch(() => { if (generation === runwayGeneration) host.textContent = 'Runway choices failed to load.'; });
  };
  const updateHint = () => {
    const towerChoice = form.querySelector<HTMLInputElement>('input[name=position][value=tower]')!;
    towerChoice.disabled = value('airport') !== 'ksfo';
    if (towerChoice.disabled && towerChoice.checked) form.querySelector<HTMLInputElement>('input[name=position][value=legacy]')!.checked = true;
    form.querySelector<HTMLElement>('[data-position-hint]')!.textContent = value('airport') === 'ksfo' ? 'Tower: manage the runway with AI approach and ground. Original combined: control all flights as before.' : 'The tower operational profile is currently available at KSFO. This airport retains original combined control.';
    const mode = value('mode');
    const t = TRAFFIC[value('traffic') as Traffic];
    const scenario = SCENARIOS.find((c) => c.id === value('scenario'));
    form.querySelector<HTMLElement>('[data-scenario-choice]')!.hidden = mode !== 'scenario';
    form.querySelector<HTMLElement>('[data-runway-choice]')!.hidden = mode === 'scenario';
    const best = challengeRecords(browserStorage())[`${value('airport')}|${value('scenario')}${value('position') === 'tower' ? `|tower-v1|${(form.elements.namedItem('coaching') as HTMLInputElement).checked ? 'coached' : 'expert'}` : ''}`];
    form.querySelector<HTMLElement>('[data-scenario-hint]')!.textContent = `${scenario?.briefing ?? ''} Challenges use mid realism with no wind, instant compliance, and runway safety enabled.${best ? ` Completed before: best ${Math.round(best)} seconds.` : ''}`;
    hint.textContent = `About ${t.arrivalsPerHour} arrivals and ${t.departuresPerHour} departures an hour`;
    modeHint.textContent = MODES.find((m) => m.value === mode)?.hint ?? '';
    layoutHint.textContent = VIEWS.find((v) => v.layout === value('layout'))?.hint ?? '';
    sum('airport', value('airport').toUpperCase());
    sum('mode', mode === 'scenario' ? scenario?.title ?? 'Challenge' : MODES.find((m) => m.value === mode)?.label ?? '');
    sum('traffic', TRAFFIC_LABEL[value('traffic') as Traffic]);
    sum('realism', mode === 'scenario' ? 'Mid' : REALISM_LABEL[value('realism') as RealismLevel]);
    sum('layout', VIEWS.find((v) => v.layout === value('layout'))?.label ?? '');
    updateConditions();
    loadRunways(value('airport'));
  };
  form.addEventListener('change', updateHint);
  updateHint();
  showStep(0, false);

  let disposed = false;
  const abort = new AbortController();
  const capability = (text: string) => { if (!disposed) form.querySelector('[data-voice-capability]')!.textContent = text; };
  const dev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
  const offline = dev ? 'Voice service is disabled or offline: run pnpm dev:voice in a second terminal. Browser voices and typed clearances remain available.' : 'AI radio is unavailable right now. Browser voices and typed clearances still work.';
  void fetch('/api/voice/capabilities', { signal: abort.signal }).then(async (r) => {
    if (!r.ok) { capability(offline); return; }
    const c = await r.json();
    capability(c.available ? 'AI radio is available: push to talk and AI generated voices.' : dev ? 'OpenAI radio is disabled or offline: set OPENAI_API_KEY and ATC_OPENAI_VOICE_ENABLED=true in .env and restart pnpm dev:voice.' : 'AI radio is not offered on this server. Browser voices and typed clearances still work.');
  }).catch(() => capability(offline));
  for (const a of AIRPORTS) {
    const el = form.querySelector<HTMLElement>(`[data-runways="${a.icao}"]`)!;
    loadAirport(a.icao)
      .then((ap) => { if (!disposed) el.textContent = `Lands ${ap.arrivalRunway}, departs ${ap.departureRunway}`; })
      .catch(() => { if (!disposed) el.textContent = 'Airport data failed to load'; });
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const arrivalRunways = selectedRunways('arrival'), departureRunways = selectedRunways('departure');
    if (!arrivalRunways.length || !departureRunways.length) {
      const runwayPicks = form.querySelector<HTMLElement>('[data-runway-picks]')!;
      runwayPicks.setAttribute('data-error', 'Choose at least one arrival and departure runway.');
      showStep(1, false);
      runwayPicks.querySelector<HTMLElement>('input')?.focus();
      return;
    }
    options.onStart({
      ...s,
      position: value('position') as 'legacy' | 'tower',
      coaching: (form.elements.namedItem('coaching') as HTMLInputElement).checked,
      airport: value('airport'),
      arrivalRunways,
      departureRunways,
      mode: value('mode') === 'scenario' ? 'scenario' : value('mode') === 'free' ? 'free' : 'shift',
      scenario: value('scenario') as SessionSettings['scenario'],
      graphicsQuality: value('graphicsQuality') === 'auto' ? 'auto' : Number(value('graphicsQuality')) as 0 | 1 | 2 | 3,
      assists: (form.elements.namedItem('assists') as HTMLInputElement).checked,
      shiftMinutes: value('mode') === 'shift60' ? 60 : value('mode') === 'shift30' ? 30 : value('mode') === 'shift15' ? 15 : s.shiftMinutes,
      traffic: value('traffic') as Traffic,
      realism: value('realism') as RealismLevel,
      layout: value('layout') as Layout,
      timeOfDay: value('timeOfDay') as TimeOfDay,
      weather: value('weather') as Weather,
      voice: (form.elements.namedItem('voice') as HTMLInputElement).checked,
      speechInput: (form.elements.namedItem('speechInput') as HTMLInputElement).checked,
      voiceImmediate: false,
      fullscreen: (form.elements.namedItem('fullscreen') as HTMLInputElement).checked,
      sound: (form.elements.namedItem('sound') as HTMLInputElement).checked,
    });
  });
  form.querySelector<HTMLElement>('input[name=mode]:checked')?.focus();
  const disposeNavigation = mountMenuNavigation(root);
  return () => { disposed = true; abort.abort(); disposeNavigation(); root.replaceChildren(); };
}
